import { useState, useMemo } from 'react';
import { Trash2, Check, Copy, CheckCheck, ArrowUpDown } from 'lucide-react';
import type { Asset } from '../lib/api';

type SortField = 'name' | 'date' | 'type';
type SortDir = 'asc' | 'desc';

interface AssetGridProps {
  assets: Asset[];
  onDelete?: (assetId: string) => void;
  onSelect?: (asset: Asset) => void;
  onAllocate?: (assetId: string) => void;
  selectable?: boolean;
  selectedId?: string;
  showOwner?: boolean;
  showSort?: boolean;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

function CopyUrlButton({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  const copy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button onClick={copy} className="rounded-lg bg-iron-950 p-1.5 text-iron-500 hover:text-forge-300 transition-colors" title="Copy URL">
      {copied ? <CheckCheck className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

const SORT_OPTIONS: { value: SortField; label: string }[] = [
  { value: 'name', label: 'Name' },
  { value: 'date', label: 'Date' },
  { value: 'type', label: 'Type' },
];

export function AssetGrid({ assets, onDelete, onSelect, onAllocate, selectable, selectedId, showOwner, showSort = true }: AssetGridProps) {
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const sorted = useMemo(() => {
    const arr = [...assets];
    arr.sort((a, b) => {
      let cmp = 0;
      if (sortField === 'name') {
        const nameA = ((a as any).name || a.assetId || '').toLowerCase();
        const nameB = ((b as any).name || b.assetId || '').toLowerCase();
        cmp = nameA.localeCompare(nameB);
      } else if (sortField === 'date') {
        cmp = (a.createdAt || 0) - (b.createdAt || 0);
      } else if (sortField === 'type') {
        cmp = (a.assetType || '').localeCompare(b.assetType || '');
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [assets, sortField, sortDir]);

  if (assets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-sm text-iron-500">No assets yet</p>
      </div>
    );
  }

  const toggleDir = () => setSortDir(d => d === 'asc' ? 'desc' : 'asc');

  return (
    <div>
      {/* Sort controls */}
      {showSort && assets.length > 1 && (
        <div className="flex items-center gap-2 mb-4">
          <span className="font-display text-[9px] font-800 uppercase tracking-[0.16em] text-iron-500">Sort</span>
          {SORT_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => {
                if (sortField === opt.value) toggleDir();
                else { setSortField(opt.value); setSortDir('asc'); }
              }}
              className={`rounded-md px-2.5 py-1 font-display text-[10px] font-800 uppercase tracking-wider transition-colors ${
                sortField === opt.value
                  ? 'bg-forge-500/15 text-forge-300 border border-forge-500/30'
                  : 'text-iron-500 border border-iron-700 hover:border-iron-600'
              }`}
            >
              {opt.label}
              {sortField === opt.value && (
                <ArrowUpDown className="inline h-3 w-3 ml-1 opacity-60" />
              )}
            </button>
          ))}
          <span className="text-[10px] text-iron-500 ml-1">{sortDir === 'asc' ? 'A-Z' : 'Z-A'}{sortField === 'date' ? (sortDir === 'asc' ? ' (oldest)' : ' (newest)') : ''}</span>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {sorted.map(asset => {
          const isSelected = selectable && selectedId === asset.assetId;
          return (
            <div
              key={asset.assetId}
              onClick={() => selectable && onSelect?.(asset)}
              className={`group relative rounded-xl border bg-iron-950 overflow-hidden transition-all ${
                isSelected ? 'border-forge-500 ring-1 ring-forge-500/30' :
                selectable ? 'border-iron-700 cursor-pointer hover:border-forge-500/50' :
                'border-iron-700'
              }`}
            >
              <div className="aspect-square bg-iron-800 flex items-center justify-center">
                <img
                  src={asset.url}
                  alt={asset.assetType}
                  className="h-full w-full object-contain p-2"
                  loading="lazy"
                />
              </div>
              <div className="p-2">
                {(asset as any).name && (
                  <p className="truncate text-xs text-white mb-0.5">{(asset as any).name}</p>
                )}
                <div className="flex items-center justify-between">
                  <span className="font-display text-[9px] font-800 uppercase tracking-[0.14em] text-forge-400">
                    {asset.assetType}
                  </span>
                  <span className="text-[10px] text-iron-500">{formatBytes(asset.sizeBytes)}</span>
                </div>
                {(asset as any).group && (
                  <p className="mt-0.5 truncate text-[10px] text-iron-500">
                    <span className="text-iron-500">grp:</span> {(asset as any).group}
                  </p>
                )}
                {showOwner && !(asset as any).group && (
                  <p className="mt-0.5 truncate text-[10px] text-iron-500">
                    {asset.pk?.replace(/^(ORG#|CLAN#|USER#|PLATFORM)/, '')}
                  </p>
                )}
                {onAllocate && (
                  <button
                    onClick={e => { e.stopPropagation(); onAllocate(asset.assetId); }}
                    className="mt-2 w-full rounded-md border border-forge-500/30 bg-forge-500/10 px-2 py-1 font-display text-[9px] font-800 uppercase tracking-wider text-forge-300 hover:bg-forge-500/20 transition-colors"
                  >
                    Assign to Org
                  </button>
                )}
              </div>

              {isSelected && (
                <div className="absolute top-2 right-2 flex h-5 w-5 items-center justify-center rounded-full bg-forge-500">
                  <Check className="h-3 w-3 text-iron-950" />
                </div>
              )}

              <div className="absolute top-2 right-2 hidden gap-1 group-hover:flex">
                <CopyUrlButton url={asset.url} />
                {onDelete && (
                  <button
                    onClick={e => { e.stopPropagation(); onDelete(asset.assetId); }}
                    className="rounded-lg bg-iron-950 p-1.5 text-iron-500 hover:text-red-400"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
